/**
 * Persisted enum member lists.
 *
 * Where the accepted baseline enumerates members, the list here is VERBATIM and nothing is added.
 * Where it does not, the list was chosen under DEV-0004's three rules and the source is marked
 * `[DEV-0004]`. Every list here has a matching CHECK constraint in db/migrations.
 *
 * §3.8 rule 3 is binding on all of them: NOT NULL, no permissive default.
 */
/** 02 §5.1 — verbatim. */
export const TENANT_STATUSES = ['active', 'trial', 'suspended', 'closed'];
/**
 * Which of those statuses permit the tenant to OPERATE. See DEV-0008.
 *
 * ── WHY IT LIVES HERE AND NOT IN `@glpoint/authz` — CM-007 ───────────────────────────────────────
 * It was declared in `entitlement-resolver.ts`, next to its first reader, which was reasonable while
 * the gate was the only thing that asked. CM-007 established that suspension must also stop the
 * customer's MACHINE — the connector envelope path — and that path is a service, which G-07 forbids
 * from importing `@glpoint/authz` at all. That refusal is correct: a service must not be able to
 * reach an authorization predicate.
 *
 * But this is not an authorization predicate. It is a PARTITION OF THE LIFECYCLE VOCABULARY declared
 * two lines above — the same kind of fact as the list itself, and it belongs with it. Moving it here
 * lets the human gate and the machine path read ONE set without either of them reaching into the
 * other's package. `@glpoint/authz` re-exports it, so its own readers are unchanged.
 *
 * A `ReadonlySet<string>` rather than `ReadonlySet<TenantStatus>` deliberately: the gate reads a
 * status that arrived from a database column as a bare `string`, and `has()` must be answerable for
 * a value that is not in the union — an unrecognised status is a DENY, never a type error nobody
 * checked for.
 */
export const TENANT_OPERATING_STATUSES = new Set(['active', 'trial']);
/**
 * 01 §2 — `tenant_kind`.
 * CF-66: this column may exist and may NEVER be read in the request path.
 * It is on the entitlement resolver's forbidden-input list and is not carried on the
 * AuthorizationContext (04 line 1316).
 */
export const TENANT_KINDS = ['cpa_firm', 'direct_business', 'internal'];
/**
 * 02 §5.2 — verbatim, including `review_required` added by Owner Decision D2.
 * Column is NOT NULL with NO DEFAULT. "D2 adds a member to the enum; it does not soften the column."
 */
export const COMPANY_LIFECYCLE_STATUSES = [
    'prospect',
    'active',
    'dormant',
    'former_client',
    'merged_into',
    'quarantined',
    'source_unavailable',
    'review_required',
];
/**
 * The lifecycle → permitted-effect mapping evaluated at gate stage 4.
 * 04 §5.3: "Expressed as a state → effect mapping evaluated at stage 4, so it is one table,
 * not a per-module judgment."
 *
 * `review_required` is DENY for every WRITE effect (D2 / 04 §5.3), with its own reason code.
 */
export const COMPANY_LIFECYCLE_PERMITS = {
    prospect: { read: true, write: true },
    active: { read: true, write: true },
    dormant: { read: true, write: false },
    former_client: { read: true, write: false },
    merged_into: { read: true, write: false },
    quarantined: { read: false, write: false },
    source_unavailable: { read: true, write: false },
    review_required: { read: true, write: false },
};
/** 02 §5.2 — verbatim. */
export const COMPANY_ENTITY_KINDS = [
    'company_ltd',
    'licensed_dealer',
    'exempt_dealer',
    'individual',
    'ngo',
    'reporting_entity',
];
/** 08 §5.1 — verbatim, plus the non-state `draft` which grants nothing. */
export const SUBSCRIPTION_STATES = [
    'draft',
    'trialing',
    'active',
    'past_due',
    'suspended',
    'cancelled',
    'expired',
];
/**
 * WHICH SUBSCRIPTION STATES ARE BILLED. **Moved here 2026-11-03, and it had two homes.**
 *
 * `trialing` IS IN THE SET and is the one worth arguing for: a trialing subscription whose period
 * has ended is billed for the days after its trial, and the trial itself prices at nothing.
 * `suspended` and `expired` are NOT: §10's third stage is re-registration, and charging a
 * suspended office would be collecting for a service it no longer has. `draft` was never opened.
 *
 * ── WHY IT LIVES IN CONTRACTS ────────────────────────────────────────────────────────────────
 *
 * It was a constant in `packages/db`, which was correct while the only question was *"which rows
 * does the due query return"*. On 2026-11-03 the Owner asked the table to list EVERY customer for
 * the period with the reason on each row, so the query stopped filtering on it and the SERVICE
 * started deciding with it — two readers in two packages, and the service could not import the
 * store's constant without reaching through its own port.
 *
 * ONE LIST, OR THE TWO DRIFT IN WORDING BEFORE ANYBODY FINDS THEM WRONG. The db package now
 * imports this, and so does the run.
 */
/**
 * THE SHORTEST PASSWORD THIS PRODUCT ACCEPTS. **Moved here 2026-11-04.**
 *
 * It lived in `apps/api/src/domain/invitation-token.ts`, where the SERVER could read it and the
 * SCREEN could not. So the screen said what it could: *"הסיסמה נבדקת בשרת. אם היא קצרה מדי, נאמר
 * לך זאת"* — a sentence about the mechanism, which leaves a person to guess, fail, and be told.
 * The Owner met it and read the refusal as a MAXIMUM: *"אם אני מקיש יותר מ-8 תווים לא נותן לי
 * להתקדם"*. He had typed fewer than twelve.
 *
 * A RULE THE SCREEN CANNOT STATE IS A RULE THE PERSON HAS TO DISCOVER BY FAILING. It is here so
 * both sides read the same number: a hint that said "twelve" beside a server that wanted thirteen
 * would be worse than the sentence it replaced.
 *
 * TWELVE, AND LENGTH IS THE WHOLE RULE. No class requirement — no uppercase, no digit, no symbol.
 * That is deliberate and it is the modern guidance: composition rules push people towards
 * `Password1!` and a long passphrase beats a short complicated one. The screen says so, because a
 * person who expects to be asked for a symbol will invent one and remember it worse.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const BILLABLE_SUBSCRIPTION_STATES = [
    'trialing',
    'active',
    'past_due',
    'cancelled',
];
/**
 * 08 §6.0 (ruled by D11) + §6.0.1 — which action classes each subscription state permits.
 *
 * `suspended` denies EVERY action class — D11 overrode the recommendation to keep read+export.
 * The restricted account-owner retrieval is NOT in this table: it is a separate capability at
 * gate stage 6, on a different surface (see DEV-0007).
 *
 * `past_due` is the one state whose row is CONFIGURATION (E-BD-12 grace policy). The default here
 * is the strictest reading E states: full function, NO EXPANSION. Expansion is blocked by the
 * limit check, not by this table.
 */
export const SUBSCRIPTION_STATE_PERMITS = {
    draft: { read: false, write: false, export: false, deliver: false },
    trialing: { read: true, write: true, export: true, deliver: true },
    active: { read: true, write: true, export: true, deliver: true },
    past_due: { read: true, write: true, export: true, deliver: true },
    suspended: { read: false, write: false, export: false, deliver: false },
    // 08 §6.0.1 gives `cancelled` TWO rows and this table can only hold one. This row is the FIRST —
    // "cancelled, UNTIL PERIOD END". The second — "cancelled, after period end → expired" — is a
    // TIME condition, and the resolver applies it before consulting this table: past `endsAt`, a
    // `cancelled` subscription is evaluated as `expired`. Reading this row on its own is wrong.
    cancelled: { read: true, write: true, export: true, deliver: true },
    expired: { read: false, write: false, export: false, deliver: false },
};
/**
 * States whose permissions are bounded by `subscription.ends_at` rather than by state alone.
 *
 * 08 §6.0.1 marks both of these rows "(derived)". A derived row that is never derived is a
 * fail-open, so the resolver must apply the time bound; `SUBSCRIPTION_STATE_PERMITS` alone is not
 * a sufficient reader of that table.
 */
export const TIME_BOUNDED_SUBSCRIPTION_STATES = new Set(['cancelled']);
/** 01 §A5 / 02 §5.2 — verbatim. See DEV-0002 for why `approve` and not `ADMIN`. */
export const ACCESS_LEVELS = ['read', 'write', 'approve'];
/** Which access levels satisfy a given action class at gate stage 4. */
export const ACCESS_LEVEL_PERMITS = {
    read: { read: true, write: false, export: true, deliver: false },
    write: { read: true, write: true, export: true, deliver: true },
    approve: { read: true, write: true, export: true, deliver: true },
};
/** 01 §A3 — membership status, per membership, never on the identity. */
export const MEMBERSHIP_STATUSES = ['invited', 'active', 'suspended', 'disabled'];
/** A membership in these states consumes a seat (01 §A3). A platform role consumes no seat. */
export const SEAT_CONSUMING_MEMBERSHIP_STATUSES = new Set([
    'invited',
    'active',
]);
/** [DEV-0004] — the ERD says "status enum" without members for app_user. */
export const APP_USER_STATUSES = ['invited', 'active', 'suspended', 'disabled'];
/** [DEV-0004] — grant rows are revoked, never deleted (CG-5: removing one row changes one pair). */
export const GRANT_STATUSES = ['active', 'revoked'];
/** [DEV-0004] — catalogue rows retire, they do not delete (§8: no physical delete). */
export const CATALOGUE_STATUSES = ['active', 'planned', 'retired'];
/** 02 §5.2 — verbatim (05 §3). */
export const EXTERNAL_SYSTEM_NAMESPACE_CLASSES = [
    'source_system',
    'statutory',
    'legacy_glpoint',
    'partner',
];
/** 02 §5.2 / 05 §3 — verbatim. */
export const EXTERNAL_SYSTEM_STATUSES = ['active', 'frozen', 'retired'];
/**
 * 05 §4.2 — verbatim, with the resolver-usability property the document attaches to each member.
 * `legacy_unverified` is displayed but NEVER used to resolve. `proposed` and `disputed` are not usable —
 * a disputed identifier fails closed.
 */
export const IDENTIFIER_CONFIDENCES = [
    'authoritative',
    'human_confirmed',
    'derived_verified',
    'legacy_unverified',
    'proposed',
    'disputed',
];
export const RESOLVABLE_CONFIDENCES = new Set([
    'authoritative',
    'human_confirmed',
    'derived_verified',
]);
/** 05 §4.2 — verbatim. */
export const SOURCE_OF_ASSERTIONS = [
    'source_system_authoritative',
    'source_system_derived',
    'human',
    'import_legacy',
    'discovery_proposed',
];
/** 05 §6.2 — verbatim. */
export const CONFLICT_KINDS = [
    'identifier_ambiguous',
    'duplicate_identifier',
    'unmapped_source',
    'group_used_as_company',
    // 05 §6.3 case C3, verbatim: "Modelled as: conflict_kind = 'group_key_corrupted'". It is in §6.3
    // and not in §6.2's table, which is how it was missed. Without it, C3 — the irreversible Hebrew
    // mojibake `fs::????? ????? ?.?` sitting inside the live authorization store — is UNREPRESENTABLE,
    // and migration precondition M-7 ("C1, C2, C3 are loaded as conflict rows BEFORE any dependent
    // data moves") cannot be satisfied.
    'group_key_corrupted',
];
/** 05 §6.1 — verbatim. */
export const CONFLICT_STATUSES = ['open', 'resolved', 'accepted_permanent'];
/** 05 §6.1 — verbatim. */
export const CONFLICT_DETECTED_BY = ['migration', 'import', 'constraint_violation', 'human'];
/** 08 §12A.5 — verbatim. */
/*
 * `fixed_price` ADDED BY 0037, and it is a REPLACEMENT price rather than a reduction.
 *
 * The Owner's §5: *"מחיר קבוע מופחת לתקופה, כגון 50 ש״ח לחודש למשך חודשיים."* Its amount lives
 * in `fixed_price_amount`, NOT in `discount_amount`: that column is declared as an amount
 * SUBTRACTED, and anything totalling what a customer saved would add a replacement price as
 * though it were a reduction.
 */
export const DISCOUNT_KINDS = ['percentage', 'fixed_amount', 'full_waiver', 'fixed_price'];
/** 02 §5.3 subscription_discount — verbatim. */
export const DISCOUNT_SCOPES = ['subscription', 'sku'];
/** 02 §5.3 tenant_module_entitlement — verbatim. */
export const ENTITLEMENT_SOURCES = ['plan', 'trial', 'add_on', 'manual_override'];
/** 08 §4.2.1 — the seven seeded charge dimensions. A catalogue table, NOT an enum (D4: adding one is an INSERT). */
export const SEEDED_CHARGE_DIMENSIONS = [
    'tenant',
    'company',
    'company_year',
    'cfo_unit',
    'user',
    'grant_cycle',
    'source',
];
/**
 * The UNION of two baseline lists that disagree — see DEV-0004 §2.
 *   02 §5.7 gives:  user · job · connector · system
 *   04 §8.2 gives:  user · platform · service · job · anonymous
 * Neither is a superset. Dropping a member from either would make an event the other document
 * requires unrecordable, and an audit write that cannot happen is one 04 §8.2 forbids.
 */
export const AUDIT_ACTOR_KINDS = [
    'user',
    'platform',
    'service',
    'job',
    'connector',
    'system',
    'anonymous',
];
/**
 * What a governed action was performed ON. **CM-010.**
 *
 * Verbatim from `CK_audit_entity_type` in migration 0004, and that is the point: the constraint
 * enumerated twenty-three kinds and the product emitted exactly one — `'route'` — with a NULL id, on
 * every row it could produce. An audit model that can say *"an authorization decision was made"* and
 * never *"who touched this company"* is an authorization log wearing an audit table, which is what
 * 0004's own header says it must not be.
 *
 * `'route'` stays in the list and keeps its meaning: it is the AUTHORIZATION decision the gate
 * records, which genuinely has no domain target. It is no longer the only member anything writes.
 */
export const AUDIT_ENTITY_TYPES = [
    'route',
    'tenant',
    'app_user',
    'membership',
    'role',
    'capability',
    'company',
    'company_identifier',
    'company_group',
    'company_grant',
    'session',
    'subscription',
    'subscription_sku',
    'subscription_discount',
    'company_sku_assignment',
    'tenant_module_entitlement',
    'support_grant',
    'secret_ref',
    'approval_policy',
    'job_run',
    'plan',
    'product_sku',
    /*
     * 0033 — §12.3. A message this product sent, with the actor who caused it.
     *
     * `entity_uid` is NULL on these rows (a Message-ID is not a GUID) and the KIND travels in
     * `action` as `mail.<kind>.requested`, which 0004 left unconstrained for exactly this. The
     * nearest existing members are all wrong in the same way: a `signup_verification` message
     * concerns no `app_user`, no `tenant` and no `membership`, because at that moment none of the
     * three exists yet.
     */
    'mail',
];
/** 04 §8.2 — verbatim. */
export const AUDIT_OUTCOMES = ['allow', 'deny'];
/**
 * WHAT A SUBSCRIPTION-SKU WRITE ACTUALLY DID. **F1R-06.**
 *
 * Here rather than in the store or in the service port, because both need the same four words and
 * a union declared on one side and restated on the other is two vocabularies that drift.
 *
 * The caller needs this and cannot derive it. A `201 Created` for a retry tells a client it created
 * something it did not; a `200 OK` for a real change tells it nothing happened. Four states, four
 * different sentences to the operator:
 *
 *   · `created`    — there was no live line for this SKU, and now there is;
 *   · `changed`    — the live line moved to a new quantity or a new price. The previous state was
 *                    closed and kept, and `entitlement_version` moved exactly once;
 *   · `unchanged`  — the line already held exactly this state. A RETRY: nothing written, no bump,
 *                    and the SAME version returned, so a client that lost the first response can
 *                    carry on rather than start again;
 *   · `concurrent` — another request changed this line between the read and the write. Nothing was
 *                    written, and the caller must re-read rather than assume either outcome.
 */
export const SKU_WRITE_OUTCOMES = ['created', 'changed', 'unchanged', 'concurrent'];
//# sourceMappingURL=enums.js.map